import { useEffect, useRef, useState } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

export default function ProcessPage() {
  const [isVisible, setIsVisible] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      number: 1,
      title: 'Audit & Discovery',
      description: 'We analyse your current site (if you have one), understand your business goals, and identify opportunities for growth.',
      details: [
        'Comprehensive website audit',
        'Competitor analysis',
        'Target audience research',
        'Goal setting & KPI definition'
      ],
      timeline: '3-5 days'
    },
    {
      number: 2,
      title: 'Design & Launch',
      description: 'We create a custom website tailored to your brand, optimised for conversions, and launch it within 2-3 weeks.',
      details: [
        'Custom design mockups',
        'Brand integration',
        'Content creation & optimisation',
        'Testing & quality assurance',
        'Launch & go-live'
      ],
      timeline: '2-3 weeks'
    },
    {
      number: 3,
      title: 'Ongoing Optimisation',
      description: 'We continuously monitor, update, and improve your site to ensure peak performance and maximum results.',
      details: [
        'Performance monitoring',
        'Regular content updates',
        'Security & maintenance',
        'Conversion rate optimisation',
        'Monthly reporting'
      ],
      timeline: 'Continuous'
    },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d5setdj99ei0h7kmahig', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => {
          setShowContactForm(false);
          setFormStatus('idle');
        }, 3000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-[#0a0a0a]">
        <section ref={sectionRef} className="py-16 sm:py-20 md:py-32 lg:py-40 px-4">
          <div className="max-w-[1100px] mx-auto">
            <div className="text-center mb-12 md:mb-20">
              <h1 className="text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">How It Works</h1>
              <p className="text-base md:text-lg text-white/50 max-w-2xl mx-auto px-4">
                A simple, proven process that gets you from idea to launch quickly
              </p>
            </div>

            <div className="relative flex flex-col md:flex-row items-start justify-between gap-12 md:gap-8 mb-16 md:mb-24">
              {steps.map((step, index) => (
                <div key={index} className="flex flex-col items-center w-full md:w-[320px]">
                  {/* Step Number */}
                  <div
                    className={`w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-[#8b2bd9] to-[#d946ef] flex items-center justify-center mb-6 md:mb-8 transition-all duration-700 ${
                      isVisible ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
                    }`}
                    style={{ transitionDelay: `${index * 200}ms` }}
                  >
                    <span className="text-white text-3xl md:text-4xl font-bold">{step.number}</span>
                  </div>

                  {/* Content */}
                  <div
                    className={`flex flex-col items-center transition-all duration-700 ${
                      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                    style={{ transitionDelay: `${index * 200 + 300}ms` }}
                  >
                    <h3 className="text-xl sm:text-2xl md:text-3xl font-semibold text-white mb-4 md:mb-5 text-center whitespace-nowrap">{step.title}</h3>
                    <p className="text-white/65 leading-relaxed text-center text-sm md:text-base max-w-[280px] md:w-[240px] mx-auto mb-6">
                      {step.description}
                    </p>

                    {/* Timeline Badge */}
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-full px-4 py-2 mb-6">
                      <span className="text-purple-300 text-sm font-semibold">{step.timeline}</span>
                    </div>

                    {/* Details List */}
                    <div className="space-y-2 w-full">
                      {step.details.map((detail, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <i className="ri-check-line text-purple-400 text-lg mt-0.5 flex-shrink-0"></i>
                          <span className="text-white/50 text-sm text-left">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Additional Information Section */}
            <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-3xl p-8 md:p-12 mb-16 md:mb-24">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 text-center">
                What Makes Our Process Different?
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-speed-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Fast Turnaround</h3>
                    <p className="text-white/60 text-sm leading-relaxed">
                      Most websites are live within 2-3 weeks. We work efficiently without compromising quality, getting you online and generating leads quickly.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-heart-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Dedicated Support</h3>
                    <p className="text-white/60 text-sm leading-relaxed">
                      You'll have a dedicated account manager throughout the entire process. No automated responses - just real people who care about your success.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-line-chart-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Data-Driven Approach</h3>
                    <p className="text-white/60 text-sm leading-relaxed">
                      Every decision is backed by data and industry best practices. We use analytics and A/B testing to ensure your website performs at its best.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">No Hidden Costs</h3>
                    <p className="text-white/60 text-sm leading-relaxed">
                      Transparent pricing from day one. What you see is what you pay - no surprise fees or hidden charges down the line.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="max-w-[900px] mx-auto">
              <div className="bg-gradient-to-br from-[#8b2bd9] to-[#d946ef] rounded-3xl p-8 md:p-16 text-center">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">
                  Ready to Get Started?
                </h2>
                <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-8 md:mb-10">
                  Let's begin the process for your business today. Get in touch with us now and we'll schedule your free audit to kickstart your website transformation.
                </p>
                <button
                  onClick={() => setShowContactForm(true)}
                  className="px-8 md:px-10 py-4 md:py-5 bg-white text-[#8b2bd9] font-bold rounded-xl hover:bg-white/90 transition-all duration-300 transform hover:scale-105 cursor-pointer text-base md:text-lg whitespace-nowrap shadow-2xl"
                >
                  Get In Touch Now
                </button>
                <p className="text-white/80 text-sm mt-6">
                  <i className="ri-time-line mr-2"></i>
                  Response within 48 hours • No obligation • Free consultation
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Popup */}
        {showContactForm && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 backdrop-blur-lg rounded-3xl p-6 md:p-8 max-w-2xl w-full max-h-[85vh] overflow-y-auto border border-purple-500/20">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl md:text-3xl font-bold text-white">Get In Touch</h3>
                <button
                  onClick={() => setShowContactForm(false)}
                  className="text-gray-400 hover:text-white text-2xl cursor-pointer transition-colors duration-300 w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>

              {formStatus === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-check-line text-green-400 text-4xl"></i>
                  </div>
                  <h4 className="text-2xl font-bold text-white mb-3">Message Sent Successfully!</h4>
                  <p className="text-white/70">We'll get back to you within 48 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="process-contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-white mb-2 text-sm font-semibold">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        disabled={formStatus === 'submitting'}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition-colors duration-300 disabled:opacity-50"
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-white mb-2 text-sm font-semibold">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        disabled={formStatus === 'submitting'}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition-colors duration-300 disabled:opacity-50"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-white mb-2 text-sm font-semibold">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      disabled={formStatus === 'submitting'}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition-colors duration-300 disabled:opacity-50"
                      placeholder="+44 7700 900000"
                    />
                  </div>

                  <div>
                    <label htmlFor="website" className="block text-white mb-2 text-sm font-semibold">
                      Current Website (if any)
                    </label>
                    <input
                      type="url"
                      id="website"
                      name="website"
                      disabled={formStatus === 'submitting'}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition-colors duration-300 disabled:opacity-50"
                      placeholder="https://yourwebsite.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-white mb-2 text-sm font-semibold">
                      Tell us about your project *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      disabled={formStatus === 'submitting'}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition-colors duration-300 resize-none disabled:opacity-50"
                      placeholder="What are your goals for this website? What challenges are you facing?"
                    ></textarea>
                    <p className="text-white/40 text-xs mt-2">Maximum 500 characters</p>
                  </div>

                  {formStatus === 'error' && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
                      <p className="text-red-400">Something went wrong. Please try again.</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-500 text-white font-bold rounded-xl hover:from-purple-500 hover:to-blue-400 transition-all duration-300 transform hover:scale-105 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        )}

        <Footer />
      </div>
    </>
  );
}
